import type { FeedbackCue, FocusDecision, FocusDestinations, FocusDirection, FocusNodeSnapshot, FocusRect, FocusRole } from './core';
import { resolveFocusMove } from './core';
import { Platform } from 'react-native';

export type FocusInputModality = 'pointer' | 'keyboard' | 'gamepad' | 'native-tv';
export type FocusHandle = {
  focus?: () => void;
  click?: () => void;
  requestTVFocus?: () => void;
  measureInWindow?: (callback: (x: number, y: number, width: number, height: number) => void) => void;
  scrollIntoView?: (options?: any) => void;
  getBoundingClientRect?: () => FocusRect & { right?: number; bottom?: number };
  closest?: (selector: string) => Element | null;
};

export type FocusNodeRegistration = {
  id: string;
  group: string;
  role: FocusRole;
  order?: number;
  destinations?: FocusDestinations;
  preferred?: boolean;
  pointerOnly?: boolean;
  disabled?: boolean;
  activate?: () => void;
  move?: (direction: FocusDirection) => boolean | void;
  back?: () => boolean;
  handle: FocusHandle;
  scope?: string;
};

export type FocusTransition = {
  fromId: string | null;
  toId: string | null;
  direction?: FocusDirection;
  cue: FeedbackCue;
  reason: FocusDecision['reason'] | 'activation' | 'back' | 'native-focus';
  modality: FocusInputModality;
  route: string;
  candidates?: FocusDecision['candidates'];
};

const nodes = new Map<string, FocusNodeRegistration>();
const listeners = new Set<(transition: FocusTransition) => void>();
const modalityListeners = new Set<(next: FocusInputModality) => void>();
const routeHistory = new Map<string, string>();
const scopeStack: string[] = ['app'];
const scopeBackHandlers = new Map<string, () => boolean | void>();
const scopeParentIds = new Map<string, string | null>();
let appBackHandler: (() => boolean | void) | undefined;
let appActivateHandler: (() => boolean | void) | undefined;
let activeId: string | null = null;
let currentRoute = 'Unknown';
let currentRailId = 'shell.rail.home';
let modality: FocusInputModality = 'pointer';
let instanceCounter = 0;
let orderCounter = 0;
let lastTransition: FocusTransition | null = null;
let transitionCount = 0;
let lazyFocusPending = false;
let nativeLazyFocusMove: ((direction: FocusDirection) => boolean | void) | undefined;
let nativeLazyRetrying = false;
let nativeLazyRetryAttempts = 0;
const fallbackWebIds = new WeakMap<object, string>();

export function getFocusTransitionCount() {
  return transitionCount;
}

export function getFocusRoute() {
  return currentRoute;
}

/** Register a bounded native-TV row virtualization handoff. */
export function setNativeLazyFocusMove(handler?: (direction: FocusDirection) => boolean | void) {
  nativeLazyFocusMove = handler;
  return () => {
    if (nativeLazyFocusMove === handler) nativeLazyFocusMove = undefined;
  };
}

export function hasNativeFocusDestination(direction: FocusDirection) {
  const active = activeId ? nodes.get(activeId) : undefined;
  const targetId = active?.destinations?.[direction];
  const target = targetId ? nodes.get(targetId) : undefined;
  return Boolean(target && target.scope === scopeStack[scopeStack.length - 1] && !target.disabled);
}

export function createFocusInstanceId(seed?: string) {
  instanceCounter += 1;
  const normalized = (seed ?? 'control').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-|-$/g, '');
  return `${normalized || 'control'}-${instanceCounter}`;
}

export function registerFocusNode(registration: FocusNodeRegistration) {
  const existing = nodes.get(registration.id);
  nodes.set(registration.id, { ...registration, scope: registration.scope ?? scopeStack[scopeStack.length - 1], order: existing?.order ?? registration.order ?? orderCounter++ });
  return () => {
    if (nodes.get(registration.id)?.handle === registration.handle) nodes.delete(registration.id);
    if (activeId === registration.id) activeId = null;
  };
}

export function setFocusRoute(route: string, railId = 'shell.rail.home') {
  if (currentRoute !== route && activeId) routeHistory.set(currentRoute, activeId);
  currentRoute = route;
  currentRailId = railId;
}

export function pushFocusScope(scope: string, onBack?: () => boolean | void) {
  if (scopeStack[scopeStack.length - 1] !== scope) {
    scopeStack.push(scope);
    scopeParentIds.set(scope, activeId);
  }
  if (onBack) scopeBackHandlers.set(scope, onBack);
}

export function popFocusScope(scope?: string) {
  if (scopeStack.length <= 1) return;
  if (!scope || scopeStack[scopeStack.length - 1] === scope) {
    const popped = scopeStack.pop();
    if (popped) {
      scopeBackHandlers.delete(popped);
      scopeParentIds.delete(popped);
    }
  }
}

export function setAppBackHandler(handler?: () => boolean | void) {
  appBackHandler = handler;
  return () => {
    if (appBackHandler === handler) appBackHandler = undefined;
  };
}

export function setAppActivateHandler(handler?: () => boolean | void) {
  appActivateHandler = handler;
  return () => {
    if (appActivateHandler === handler) appActivateHandler = undefined;
  };
}

function dismissFocusScope() {
  if (scopeStack.length <= 1) return false;
  const scope = scopeStack[scopeStack.length - 1];
  const handler = scopeBackHandlers.get(scope);
  if (handler && handler() === false) return false;
  const parentId = scopeParentIds.get(scope) ?? null;
  popFocusScope(scope);
  if (parentId) focusRegisteredNodeFromBack(parentId);
  return true;
}

export function setFocusModality(next: FocusInputModality) {
  if (modality === next) return;
  modality = next;
  modalityListeners.forEach((listener) => listener(next));
}

export function subscribeFocusModality(listener: (next: FocusInputModality) => void) {
  modalityListeners.add(listener);
  return () => { modalityListeners.delete(listener); };
}

export function getFocusModality() {
  return modality;
}

export function subscribeFocusTransitions(listener: (transition: FocusTransition) => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function emit(transition: FocusTransition) {
  lastTransition = transition;
  transitionCount += 1;
  if (Platform.OS === 'web' && typeof globalThis !== 'undefined') {
    (globalThis as typeof globalThis & { __JELLYFLIX_TV_FOCUS__?: unknown }).__JELLYFLIX_TV_FOCUS__ = getFocusDebugState();
  }
  listeners.forEach((listener) => listener(transition));
}

export function noteFocused(id: string, nativeEvent = false) {
  if (!nodes.has(id) || activeId === id) return;
  const previous = activeId;
  activeId = id;
  routeHistory.set(currentRoute, id);
  if (modality !== 'pointer') {
    const from = previous ? nodes.get(previous) : undefined;
    const to = nodes.get(id);
    emit({
      fromId: previous,
      toId: id,
      cue: from?.group === to?.group ? 'focus-item' : 'focus-group',
      reason: nativeEvent ? 'native-focus' : 'native-focus',
      modality,
      route: currentRoute,
    });
  }
}

export function noteBlurred(id: string) {
  if (activeId === id && !nodes.has(id)) activeId = null;
}

export function noteActivated(id: string) {
  if (modality !== 'pointer') emit({ fromId: activeId, toId: id, cue: 'activate', reason: 'activation', modality, route: currentRoute });
}

/** Activate the control currently owned by the shared Web focus graph. */
export function activateFocusedNode() {
  if (appActivateHandler && appActivateHandler() !== false) return true;
  if (!activeId) return false;
  const target = nodes.get(activeId);
  const element = target?.handle as FocusHandle & { click?: () => void } | undefined;
  if (!target || target.disabled) return false;
  if (target.activate) {
    noteActivated(target.id);
    target.activate();
  } else if (element?.click) {
    noteActivated(target.id);
    element.click();
  }
  else return false;
  return true;
}

export function backFocusedNode() {
  const target = activeId ? nodes.get(activeId) : undefined;
  if (target?.back && !target.disabled && target.back() !== false) return true;
  const handler = scopeBackHandlers.get(scopeStack[scopeStack.length - 1]);
  if (handler) return dismissFocusScope();
  return appBackHandler ? appBackHandler() !== false : false;
}

export function focusRegisteredNodeFromBack(id: string) {
  const target = nodes.get(id);
  if (!target || target.disabled) return false;
  const fromId = activeId;
  activeId = target.id;
  routeHistory.set(currentRoute, target.id);
  focusHandle(target, typeof document !== 'undefined');
  emit({ fromId, toId: target.id, cue: fromId && nodes.get(fromId)?.group === target.group ? 'focus-item' : 'focus-group', reason: 'back', modality, route: currentRoute });
  return true;
}

/** Focus a semantic control revealed by an activation, such as card actions. */
export function focusRegisteredNode(id: string) {
  const target = nodes.get(id);
  if (!target || target.disabled) return false;
  const fromId = activeId;
  activeId = target.id;
  routeHistory.set(currentRoute, target.id);
  focusHandle(target, typeof document !== 'undefined');
  emit({ fromId, toId: target.id, cue: fromId && nodes.get(fromId)?.group === target.group ? 'focus-item' : 'focus-group', reason: 'activation', modality, route: currentRoute });
  return true;
}

export function focusPreferredNode() {
  const scope = scopeStack[scopeStack.length - 1];
  const candidates = Array.from(nodes.values()).filter((node) => node.scope === scope && !node.disabled);
  const preferred = candidates.find((node) => node.preferred) ?? candidates.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
  return preferred ? focusRegisteredNode(preferred.id) : false;
}

export function noteBack() {
  emit({ fromId: activeId, toId: activeId, cue: 'back', reason: 'back', modality, route: currentRoute });
}

/** Return a same-route sidebar activation to the top of the current page. */
export function scrollWebContentToTop() {
  if (typeof document === 'undefined') return false;
  let changed = false;
  const root = document.scrollingElement;
  if (root && root.scrollTop > 0) {
    root.scrollTop = 0;
    changed = true;
  }
  for (const element of Array.from(document.querySelectorAll<HTMLElement>('*'))) {
    const style = window.getComputedStyle(element);
    if (style.overflowY === 'visible' || element.scrollHeight <= element.clientHeight || element.scrollTop <= 0) continue;
    element.scrollTop = 0;
    changed = true;
  }
  return changed;
}

function rectFor(handle: FocusHandle): FocusRect | null {
  const rect = handle.getBoundingClientRect?.();
  if (!rect) return null;
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

function visibleWebNode(node: FocusNodeRegistration): FocusNodeSnapshot | null {
  const handle = node.handle as FocusHandle & { isConnected?: boolean; getAttribute?: (name: string) => string | null };
  const rect = rectFor(handle);
  const activeDialog = typeof document !== 'undefined' ? document.querySelector('[data-testid="tv-focus-dialog"], [role="dialog"][aria-modal="true"], [aria-modal="true"]') : null;
  if (!rect || node.scope !== scopeStack[scopeStack.length - 1] || node.disabled || node.pointerOnly && (modality === 'gamepad' || modality === 'native-tv') || handle.isConnected === false || handle.getAttribute?.('aria-disabled') === 'true' || (activeDialog && !handle.closest?.('[data-testid="tv-focus-dialog"], [role="dialog"][aria-modal="true"], [aria-modal="true"]'))) return null;
  return { id: node.id, group: node.group, role: node.role, rect, order: node.order ?? 0, destinations: node.destinations, disabled: node.disabled, scope: node.scope };
}

function syncWebFallbackNodes() {
  if (typeof document === 'undefined') return;
  const registeredHandles = new Set(Array.from(nodes.values()).map((node) => node.handle));
  const elements = Array.from(document.querySelectorAll<HTMLElement>('input, textarea, select, [contenteditable="true"], [role="slider"]'));
  for (const element of elements) {
    if (registeredHandles.has(element)) continue;
    let id = fallbackWebIds.get(element);
    if (!id) {
      id = createFocusInstanceId(element.getAttribute('aria-label') ?? element.getAttribute('placeholder') ?? element.tagName);
      fallbackWebIds.set(element, id);
    }
    nodes.set(id, {
      id,
      group: 'route.inputs',
      role: element.getAttribute('role') === 'slider' ? 'slider' : 'input',
      order: orderCounter++,
      disabled: element.hasAttribute('disabled'),
      handle: element,
      scope: scopeStack[scopeStack.length - 1],
    });
  }
  for (const [id, node] of nodes) {
    const handle = node.handle as FocusHandle & { isConnected?: boolean };
    if (fallbackWebIds.has(handle as object) && handle.isConnected === false) nodes.delete(id);
  }
}

function focusHandle(node: FocusNodeRegistration, web: boolean, direction?: FocusDirection, fromNode?: FocusNodeRegistration) {
  if (web) {
    const element = node.handle as FocusHandle & HTMLElement;
    // React Native Web's ScrollView can leave an entered row technically
    // visible at the edge of the viewport. Reposition its real scroll parents
    // explicitly so Web matches the native TV focus-entry behavior.
    if (typeof document !== 'undefined' && element instanceof HTMLElement) {
      const scrollParents: HTMLElement[] = [];
      const horizontalScrollParents: HTMLElement[] = [];
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        const scrollable = style.overflowY !== 'visible' && parent.scrollHeight > parent.clientHeight;
        if (scrollable) scrollParents.push(parent);
        const horizontallyScrollable = style.overflowX !== 'visible' && parent.scrollWidth > parent.clientWidth;
        if (horizontallyScrollable) horizontalScrollParents.push(parent);
        parent = parent.parentElement;
      }
      // Entering Continue Watching is the home browse anchor. Restore the
      // page to its top here so the complete hero-to-first-row composition is
      // visible when focus leaves the rail/hero area. The hero itself should
      // not unexpectedly reset the page merely because its More Info action
      // received focus.
      const isContinueShelf = node.role === 'card' && node.group.startsWith('shelf.continue');
      const enteringContinueShelf = isContinueShelf && fromNode?.group !== node.group;
      if (enteringContinueShelf && direction === 'up') {
        for (const scrollParent of scrollParents) scrollParent.scrollTop = 0;
        for (const scrollParent of horizontalScrollParents) scrollParent.scrollLeft = 0;
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      } else if (node.role === 'card' && (direction === 'up' || direction === 'down') && !(isContinueShelf && direction === 'down')) {
        for (const scrollParent of scrollParents) {
          const target = element.getBoundingClientRect();
          const viewport = scrollParent.getBoundingClientRect();
          scrollParent.scrollTop += target.top + target.height / 2 - (viewport.top + viewport.height / 2);
        }
      }
      // Focus scale and card shadows must remain inside the shelf viewport;
      // horizontal card movement should never recenter the page vertically.
      if (node.role === 'card') {
        for (const scrollParent of horizontalScrollParents) {
          const target = element.getBoundingClientRect();
          const viewport = scrollParent.getBoundingClientRect();
          const inset = 4;
          const targetRight = target.right ?? target.left + target.width;
          if (target.left < viewport.left + inset) scrollParent.scrollLeft += target.left - (viewport.left + inset);
          else if (targetRight > viewport.right - inset) scrollParent.scrollLeft += targetRight - (viewport.right - inset);
        }
      }
      element.focus({ preventScroll: true });
      if (node.role !== 'hero' && node.role !== 'card') element.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      return;
    }
    node.handle.focus?.();
    node.handle.scrollIntoView?.({ block: node.role === 'hero' ? 'start' : node.role === 'card' ? 'center' : 'nearest', inline: 'nearest' });
  } else if (node.handle.requestTVFocus) node.handle.requestTVFocus();
  else node.handle.focus?.();
}

function requestLazyFocusMove(direction: FocusDirection) {
  if (typeof document === 'undefined' || typeof window === 'undefined' || lazyFocusPending) return false;
  const activeNode = activeId ? nodes.get(activeId) : undefined;
  const element = activeNode?.handle as FocusHandle & HTMLElement | undefined;
  if (!element || !(element instanceof HTMLElement)) return false;
  const horizontal = direction === 'left' || direction === 'right';
  const parents: HTMLElement[] = [];
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const canScroll = horizontal
      ? (style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'overlay') && parent.scrollWidth > parent.clientWidth
      : (style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'overlay') && parent.scrollHeight > parent.clientHeight;
    if (canScroll) parents.push(parent);
    parent = parent.parentElement;
  }
  const root = document.scrollingElement;
  if (!horizontal && root && root.scrollHeight > root.clientHeight) parents.push(root as HTMLElement);
  const target = [...parents].reverse().find((candidate) => {
    if (horizontal) return direction === 'right' ? candidate.scrollLeft < candidate.scrollWidth - candidate.clientWidth - 4 : candidate.scrollLeft > 4;
    const remaining = candidate.scrollHeight - candidate.clientHeight - candidate.scrollTop;
    // Home shelves have a small bottom inset. Do not consume a final Down
    // press by scrolling that inset and then re-resolving from a stale card;
    // that can make the next input look like a sideways rail escape.
    return direction === 'down' ? remaining > Math.max(96, candidate.clientHeight * 0.08) : candidate.scrollTop > 4;
  });
  if (!target) return false;
  const amount = (horizontal ? target.clientWidth : target.clientHeight) * 0.78;
  if (horizontal) target.scrollLeft += direction === 'right' ? amount : -amount;
  else target.scrollTop += direction === 'down' ? amount : -amount;
  lazyFocusPending = true;
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    lazyFocusPending = false;
    moveWebFocus(direction);
  }));
  return true;
}

export function moveWebFocus(direction: FocusDirection) {
  syncWebFallbackNodes();
  const snapshot = Array.from(nodes.values()).map(visibleWebNode).filter((node): node is FocusNodeSnapshot => node !== null);
  const preferred = routeHistory.get(currentRoute) ?? snapshot.find((node) => nodes.get(node.id)?.preferred)?.id;
  const activeRegistration = activeId ? nodes.get(activeId) : undefined;
  if (activeRegistration?.move?.(direction)) {
    emit({ fromId: activeRegistration.id, toId: activeRegistration.id, direction, cue: 'focus-item', reason: 'explicit', modality, route: currentRoute, candidates: [{ id: activeRegistration.id, score: -1 }] });
    return { destinationId: activeRegistration.id, cue: 'focus-item' as const, reason: 'explicit' as const, candidates: [{ id: activeRegistration.id, score: -1 }] };
  }
  const explicitTargetId = activeRegistration?.destinations?.[direction];
  const explicitTarget = explicitTargetId ? nodes.get(explicitTargetId) : undefined;
  if (explicitTarget && explicitTarget.scope === scopeStack[scopeStack.length - 1] && !explicitTarget.disabled && !(explicitTarget.pointerOnly && (modality === 'gamepad' || modality === 'native-tv'))) {
    const fromId = activeId;
    activeId = explicitTarget.id;
    routeHistory.set(currentRoute, explicitTarget.id);
    focusHandle(explicitTarget, true, direction, activeRegistration);
    const directDecision = { destinationId: explicitTarget.id, cue: explicitTarget.group === activeRegistration?.group ? 'focus-item' as const : 'focus-group' as const, reason: 'explicit' as const, candidates: [{ id: explicitTarget.id, score: -1 }] };
    emit({ fromId, toId: directDecision.destinationId, direction, cue: directDecision.cue, reason: directDecision.reason, modality, route: currentRoute, candidates: directDecision.candidates });
    return directDecision;
  }
  const railEscape = scopeStack.length === 1 && direction === 'left' && activeRegistration?.role !== 'rail' ? nodes.get(currentRailId) : undefined;
  if (railEscape && !railEscape.disabled && !(railEscape.pointerOnly && (modality === 'gamepad' || modality === 'native-tv'))) {
    const fromId = activeId;
    activeId = railEscape.id;
    routeHistory.set(currentRoute, railEscape.id);
    focusHandle(railEscape, true, direction, activeRegistration);
    const railDecision = { destinationId: railEscape.id, cue: 'focus-group' as const, reason: 'rail' as const, candidates: [{ id: railEscape.id, score: -1 }] };
    emit({ fromId, toId: railDecision.destinationId, direction, cue: railDecision.cue, reason: railDecision.reason, modality, route: currentRoute, candidates: railDecision.candidates });
    return railDecision;
  }
  const decision = resolveFocusMove(snapshot, activeId, direction, preferred);
  const fromId = activeId;
  const fromNode = fromId ? nodes.get(fromId) : undefined;
  if (decision.destinationId) {
    const target = nodes.get(decision.destinationId);
    if (target) {
      activeId = target.id;
      routeHistory.set(currentRoute, target.id);
      focusHandle(target, true, direction, fromNode);
    }
  }
  if (!decision.destinationId && requestLazyFocusMove(direction)) {
    // A virtualized shelf can need one scroll pass before its next card is
    // mounted. Still emit the group cue now so controller feedback remains
    // continuous while the resolver retries on the next animation frames.
    emit({ fromId, toId: null, direction, cue: 'focus-group', reason: 'adjacent-group', modality, route: currentRoute, candidates: decision.candidates });
    return decision;
  }
  if (!decision.destinationId && scopeStack.length > 1 && (direction === 'down' || direction === 'left' || direction === 'right') && dismissFocusScope()) return decision;
  emit({ fromId, toId: decision.destinationId, direction, cue: decision.cue, reason: decision.reason, modality, route: currentRoute, candidates: decision.candidates });
  return decision;
}

function measureNativeNode(node: FocusNodeRegistration) {
  return new Promise<FocusNodeSnapshot | null>((resolve) => {
    if (node.scope !== scopeStack[scopeStack.length - 1] || node.disabled || node.pointerOnly && (modality === 'gamepad' || modality === 'native-tv') || !node.handle.measureInWindow) {
      resolve(null);
      return;
    }
    node.handle.measureInWindow((left, top, width, height) => resolve(width > 0 && height > 0 ? {
      id: node.id,
      group: node.group,
      role: node.role,
      rect: { left, top, width, height },
      order: node.order ?? 0,
      destinations: node.destinations,
    } : null));
  });
}

/** Native adapters use the exact same geometry resolver as the Web reference implementation. */
export async function moveNativeFocus(direction: FocusDirection) {
  const activeRegistration = activeId ? nodes.get(activeId) : undefined;
  const scheduleNativeRetry = (delay: number) => {
    if (nativeLazyRetryAttempts >= 4) {
      nativeLazyRetryAttempts = 0;
      return false;
    }
    nativeLazyRetryAttempts += 1;
    nativeLazyRetrying = true;
    const retryFromId = activeId;
    setTimeout(() => {
      nativeLazyRetrying = false;
      if (activeId !== retryFromId) {
        nativeLazyRetryAttempts = 0;
        return;
      }
      void moveNativeFocus(direction);
    }, delay);
    return true;
  };
  // The hero is outside the virtualized home-row list. Mount the first row
  // before resolving its destination so Hero -> Continue is one press even
  // when the Continue card is not yet registered.
  if (currentRoute === 'Home' && !nativeLazyRetrying && direction === 'down' && activeRegistration?.id === 'home.hero.play' && nativeLazyFocusMove?.('down')) {
    scheduleNativeRetry(360);
    return { destinationId: null, cue: 'focus-group' as const, reason: 'adjacent-group' as const, candidates: [] };
  }
  const pendingSemanticTargetId = activeRegistration?.destinations?.[direction];
  if (currentRoute === 'Home' && !nativeLazyRetrying && (direction === 'up' || direction === 'down') && (!pendingSemanticTargetId || !nodes.has(pendingSemanticTargetId)) && nativeLazyFocusMove?.(direction)) {
    scheduleNativeRetry(520);
    return { destinationId: null, cue: 'focus-group' as const, reason: 'adjacent-group' as const, candidates: [] };
  }
  if (activeRegistration?.move?.(direction)) {
    emit({ fromId: activeRegistration.id, toId: activeRegistration.id, direction, cue: 'focus-item', reason: 'explicit', modality, route: currentRoute, candidates: [{ id: activeRegistration.id, score: -1 }] });
    return { destinationId: activeRegistration.id, cue: 'focus-item' as const, reason: 'explicit' as const, candidates: [{ id: activeRegistration.id, score: -1 }] };
  }
  const explicitTargetId = activeRegistration?.destinations?.[direction];
  const explicitTarget = explicitTargetId ? nodes.get(explicitTargetId) : undefined;
  if (explicitTarget && explicitTarget.scope === scopeStack[scopeStack.length - 1] && !explicitTarget.disabled) {
    nativeLazyRetryAttempts = 0;
    const fromId = activeId;
    activeId = explicitTarget.id;
    routeHistory.set(currentRoute, explicitTarget.id);
    focusHandle(explicitTarget, false, direction, activeRegistration);
    const directDecision = { destinationId: explicitTarget.id, cue: explicitTarget.group === activeRegistration?.group ? 'focus-item' as const : 'focus-group' as const, reason: 'explicit' as const, candidates: [{ id: explicitTarget.id, score: -1 }] };
    emit({ fromId, toId: directDecision.destinationId, direction, cue: directDecision.cue, reason: directDecision.reason, modality, route: currentRoute, candidates: directDecision.candidates });
    return directDecision;
  }
  // Resolve semantic destinations before measuring the screen. On native TV,
  // measurement is asynchronous and the platform may otherwise advance its
  // own focus while we are waiting, bypassing routes such as Episodes ->
  // selected season or card -> overlay action.
  const activeScope = scopeStack[scopeStack.length - 1];
  const measured = await Promise.all(
    Array.from(nodes.values())
      .filter((node) => node.scope === activeScope)
      .map(measureNativeNode),
  );
  const snapshot = measured.filter((node): node is FocusNodeSnapshot => node !== null);
  const preferred = routeHistory.get(currentRoute) ?? snapshot.find((node) => nodes.get(node.id)?.preferred)?.id;
  const railEscape = scopeStack.length === 1 && direction === 'left' && activeRegistration?.role !== 'rail' ? nodes.get(currentRailId) : undefined;
  if (railEscape && !railEscape.disabled) {
    const fromId = activeId;
    activeId = railEscape.id;
    routeHistory.set(currentRoute, railEscape.id);
    focusHandle(railEscape, false, direction, activeRegistration);
    const railDecision = { destinationId: railEscape.id, cue: 'focus-group' as const, reason: 'rail' as const, candidates: [{ id: railEscape.id, score: -1 }] };
    emit({ fromId, toId: railDecision.destinationId, direction, cue: railDecision.cue, reason: railDecision.reason, modality, route: currentRoute, candidates: railDecision.candidates });
    return railDecision;
  }
  const decision = resolveFocusMove(snapshot, activeId, direction, preferred);
  const fromId = activeId;
  if (decision.destinationId) {
    nativeLazyRetryAttempts = 0;
    const target = nodes.get(decision.destinationId);
    if (target) {
      activeId = target.id;
      routeHistory.set(currentRoute, target.id);
      focusHandle(target, false);
    }
  }
  if (!nativeLazyRetrying && !decision.destinationId && (direction === 'up' || direction === 'down') && nativeLazyFocusMove?.(direction)) {
    // The next shelf may be outside the deliberately small TV FlatList
    // window. Let the page mount exactly one adjacent row, then retry once
    // after its cards have registered with the semantic graph.
    scheduleNativeRetry(360);
  }
  if (!decision.destinationId && scopeStack.length > 1 && (direction === 'down' || direction === 'left' || direction === 'right') && dismissFocusScope()) return decision;
  emit({ fromId, toId: decision.destinationId, direction, cue: decision.cue, reason: decision.reason, modality, route: currentRoute, candidates: decision.candidates });
  return decision;
}

export function getFocusDebugState() {
  return {
    activeId,
    route: currentRoute,
    scopeStack: [...scopeStack],
    modality,
    lastTransition,
    transitionCount,
    nodes: Array.from(nodes.values()).map((node) => ({ id: node.id, group: node.group, role: node.role, preferred: node.preferred, pointerOnly: node.pointerOnly })),
  };
}
